import React from 'react';
import { Box, Button, Checkbox, Flex, Text, TextArea } from 'pounceV2';
import { Chip, useTheme } from '@mui/material';
import useModalV3 from 'Hooks/useModalV3';
import { FEEDBACK_REASONS } from './constants';

export interface AIFeedbackModalProps {
  onSubmit: (reasons: string[], additionalFeedback?: string, editedPrompt?: string) => void;
  promptText?: string;
}

const AIFeedbackModalV3: React.FC<AIFeedbackModalProps> = ({ onSubmit, promptText }) => {
  const theme = useTheme();
  const { tokens } = theme.palette;
  const { hideModal } = useModalV3();
  const [selectedReasons, setSelectedReasons] = React.useState<string[]>([]);
  const [additionalFeedback, setAdditionalFeedback] = React.useState('');
  const [includePrompt, setIncludePrompt] = React.useState(false);
  const [editedPrompt, setEditedPrompt] = React.useState(promptText || '');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const resetForm = React.useCallback(() => {
    setSelectedReasons([]);
    setAdditionalFeedback('');
    setIncludePrompt(false);
    setEditedPrompt(promptText || '');
    setIsSubmitting(false);
  }, [promptText]);

  const toggleReason = React.useCallback((reason: string) => {
    setSelectedReasons(prev => {
      if (prev.includes(reason)) {
        return prev.filter(r => r !== reason);
      }
      return [...prev, reason];
    });
  }, []);

  const handleSubmit = React.useCallback(() => {
    if (selectedReasons.length === 0 || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    const promptToSend = includePrompt ? editedPrompt.trim() || undefined : undefined;
    onSubmit(selectedReasons, additionalFeedback.trim() || undefined, promptToSend);
    resetForm();
    hideModal();
  }, [
    selectedReasons,
    additionalFeedback,
    includePrompt,
    editedPrompt,
    onSubmit,
    hideModal,
    resetForm,
    isSubmitting,
  ]);

  const handleClose = React.useCallback(() => {
    resetForm();
    hideModal();
  }, [hideModal, resetForm]);

  return (
    <Box width={450}>
      <Flex direction="column" gap={4}>
        {/* Reason selection */}
        <Box>
          <Text fontWeight="medium" mb={2}>
            What went wrong? (select all that apply)
          </Text>
          <Flex flexWrap="wrap" gap={2}>
            {FEEDBACK_REASONS.map(reason => {
              const isSelected = selectedReasons.includes(reason);
              return (
                <Chip
                  key={reason}
                  label={reason}
                  variant={isSelected ? 'filled' : 'outlined'}
                  color={isSelected ? 'primary' : 'default'}
                  onClick={() => toggleReason(reason)}
                  clickable
                />
              );
            })}
          </Flex>
        </Box>

        {/* Additional feedback */}
        <Box>
          <TextArea
            label="Additional feedback (optional)"
            value={additionalFeedback}
            onChange={e => setAdditionalFeedback(e.target.value)}
            placeholder="Tell us more about your experience..."
            rows={4}
            aria-label="Additional feedback"
          />
        </Box>

        {/* Include prompt checkbox */}
        {promptText && (
          <Box
            backgroundColor={tokens?.background?.semantic?.warning?.[2] || 'orange-100'}
            p={3}
            borderRadius={8}
          >
            <Flex direction="column" gap={2}>
              <Flex align="flex-start" gap={2}>
                <Checkbox
                  checked={includePrompt}
                  onChange={e => setIncludePrompt(e.target.checked)}
                  aria-label="Include my prompt"
                />
                <Box>
                  <Text fontWeight="medium" mb={1}>
                    Include my prompt
                  </Text>
                  <Text color={tokens?.content?.text?.semantic?.warning?.base || 'yellow-300'}>
                    Your prompt may contain sensitive information. You can edit it below to remove
                    any sensitive details before sharing.
                  </Text>
                </Box>
              </Flex>

              {includePrompt && (
                <Box>
                  <TextArea
                    label="Your prompt (editable)"
                    value={editedPrompt}
                    onChange={e => setEditedPrompt(e.target.value)}
                    placeholder="Edit your prompt to remove sensitive information..."
                    rows={4}
                    aria-label="Edit your prompt"
                  />
                </Box>
              )}
            </Flex>
          </Box>
        )}

        {/* Actions */}
        <Flex justify="flex-end" gap={2}>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={selectedReasons.length === 0 || isSubmitting}
          >
            Submit Feedback
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};

export default React.memo(AIFeedbackModalV3);
